import { Formik, Form } from 'formik';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { PENDING } from '../../../../../../design-system/constants';
import { APICallStatus } from '../../../../../../design-system/types';
import { SidePanelViewMode } from '../../../../SidePanel/types';
import {
    postPaymentScheduleRequest,
    putPaymentScheduleRequest,
} from '../../../../../../resources/contract/actions';
import { useCalculateBalanceDefaultDueDate } from '../../../../../../resources/contract/hooks';
import {
    calculateTotalAmountOfInstalment,
    paymentScheduleValidationSchema,
} from '../../../../../../resources/contract/operations';
import {
    calculateDepositDefaultDueDate,
    hasPaidOrWaitingForApprovalInstalments as hasPaidOrWaitingForApprovalInstalmentsSelector,
} from '../../../../../../resources/contract/selectors';
import {
    ContractEditStep,
    ContractId,
    InstalmentViewModel,
    PaymentScheduleViewModel,
} from '../../../../../../resources/contract/types';
import { useAppDispatch, useAppSelector } from '../../../../../../shared/redux';

import styleContract from '../../../Contract.css';
import { SelectCurrency } from '../SelectCurrency';

import { StepButtons } from '../StepButtons';

import { useGetDossierData } from '../../../../../../resources/dossier/queries';

import { InstalmentsList } from './InstalmentsList';

import s from './PaymentScheduleStep.css';

type Props = {
    contractId: ContractId;
    initialPaymentSchedule: PaymentScheduleViewModel;
    paymentScheduleRequestStatus?: APICallStatus;
    submit: typeof postPaymentScheduleRequest | typeof putPaymentScheduleRequest;
    onSuccess?: () => void;
    viewMode?: SidePanelViewMode;
};

export function PaymentSchedule({
    contractId,
    initialPaymentSchedule,
    paymentScheduleRequestStatus,
    submit,
    onSuccess,
    viewMode,
}: Props): JSX.Element {
    const dispatch = useAppDispatch();

    const { marketCode: dossierMarketCode } = useGetDossierData(contractId)?.dossier?.project || {};
    const balanceDefaultDueDate = useCalculateBalanceDefaultDueDate(contractId);

    const depositDefaultDueDate = useAppSelector(calculateDepositDefaultDueDate);
    const hasPaidOrWaitingForApprovalInstalments = useAppSelector(
        hasPaidOrWaitingForApprovalInstalmentsSelector,
    );

    const updateInstalmentsCurrency = (
        instalments: InstalmentViewModel[],
        newCurrency: string,
    ): InstalmentViewModel[] => {
        return instalments.map((instalment) => {
            return {
                ...instalment,
                price: {
                    amount: instalment.price.amount,
                    currency: newCurrency,
                },
            };
        });
    };

    return (
        <div className={s.paymentSchedule} {...(viewMode && { 'data-contract-view': viewMode })}>
            <p className={styleContract.stepSubtitle}>
                <FormattedMessage
                    id="contract.step_subtitle"
                    values={{
                        step: ContractEditStep.PAYMENT_SCHEDULE,
                    }}
                />
            </p>
            <h1 className="title-m">
                <FormattedMessage id="contract.payment_schedule.title" />
            </h1>
            <Formik
                initialValues={initialPaymentSchedule}
                enableReinitialize
                validationSchema={paymentScheduleValidationSchema}
                onSubmit={(values) => {
                    if (values.currency === undefined) {
                        return;
                    }
                    dispatch(submit(contractId, values, onSuccess));
                }}
            >
                {({ values, isValid, setFieldValue }) => (
                    <Form className={s.paymentForm}>
                        <SelectCurrency
                            wrapperClassName={s.currency}
                            name="currency"
                            disabled={hasPaidOrWaitingForApprovalInstalments}
                            inputId="currency"
                            labelTranslationId="contract.payment_schedule.currency"
                            dossierMarketCode={dossierMarketCode}
                            onChange={(value: string) => {
                                values.depositInstalments = updateInstalmentsCurrency(
                                    values.depositInstalments,
                                    value,
                                );
                                values.balanceInstalments = updateInstalmentsCurrency(
                                    values.balanceInstalments,
                                    value,
                                );
                                void setFieldValue('currency', value);
                            }}
                        />
                        <InstalmentsList
                            instalments={values.depositInstalments}
                            name="depositInstalments"
                            defaultDueDate={depositDefaultDueDate}
                        />
                        <InstalmentsList
                            instalments={values.balanceInstalments}
                            name="balanceInstalments"
                            defaultDueDate={balanceDefaultDueDate}
                        />
                        <div className={s.total}>
                            <p>
                                <FormattedMessage id="contract.payment_schedule.total_price" />
                            </p>
                            <p className="title-m" data-testid="all-instalments-total-amount">
                                <FormattedNumber
                                    value={
                                        calculateTotalAmountOfInstalment(
                                            values.depositInstalments,
                                        ) +
                                        calculateTotalAmountOfInstalment(values.balanceInstalments)
                                    }
                                    style="currency"
                                    currency={values.currency || 'EUR'}
                                />
                            </p>
                        </div>
                        <StepButtons
                            isLoading={paymentScheduleRequestStatus === PENDING}
                            isNextStepDisabled={!isValid}
                            dataTestId="button-submit-step-4"
                            viewMode={viewMode}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
}
